'use client';

import { useSubmitFeedback } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { removeBetaCookie, setPreferLegacyCookie, useClientConfig } from '@shared/utils';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { buildFeedbackContext } from '../feedback/utils/feedbackContext';
import { FeedbackError } from '../FeedbackError/FeedbackError';
import { FeedbackSuccess } from '../FeedbackSuccess/FeedbackSuccess';
import { FeedbackSheet } from '../global-sheets/feedback/FeedbackSheet/FeedbackSheet';
import { Link } from '../Link/Link';
import { Comment } from './components/comment/Comment';
import { Options } from './components/options/Options';
import { type FeedbackOption, type FeedbackOptionData, type Step, type WhyLeavingProps, STEPS } from './types';

const getFeedbackOptions = (t: ReturnType<typeof useTranslations>): FeedbackOptionData[] => [
  { value: 'PREFER_THE_OLD_DESIGN', label: t('options.options.old_design') },
  { value: 'COULD_NOT_FIND_CONTENT', label: t('options.options.not_found') },
  { value: 'HARD_TO_NAVIGATE', label: t('options.options.hard_navigate') },
  { value: 'TECHNICAL_ISSUES', label: t('options.options.technical_issues') },
  { value: 'SLOW_TO_LOAD', label: t('options.options.slow_load') },
  { value: 'OTHER', label: t('options.options.other') },
];

const WhyLeaving = ({ open: controlledOpen, onOpenChange, ...props }: WhyLeavingProps) => {
  const config = useClientConfig();
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const t = useTranslations('global_sheets.why_leaving');
  const submitFeedback = useSubmitFeedback();
  const locale = useLocale();
  const isControlled = typeof controlledOpen === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = useState(true);
  const open = isControlled ? (controlledOpen as boolean) : uncontrolledOpen;
  const [selectedOption, setSelectedOption] = useState<FeedbackOption | null>(null);
  const [comment, setComment] = useState('');
  const [currentStep, setCurrentStep] = useState<Step>(STEPS.OPTIONS);

  const resetState = () => {
    setSelectedOption(null);
    setComment('');
    setCurrentStep(STEPS.OPTIONS);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }

    if (!nextOpen) {
      resetState();
    }

    onOpenChange?.(nextOpen);
  };

  const handleOptionClick = (option: FeedbackOption) => {
    setSelectedOption(option);
    setCurrentStep(STEPS.COMMENT);
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  const handleLeaveForLegacy = () => {
    removeBetaCookie();
    setPreferLegacyCookie();
  };

  const getTrackContainer = () => {
    if (currentStep === STEPS.OPTIONS) return 'Leaving Why';
    if (currentStep === STEPS.COMMENT) return 'Leaving Comments';
    if (currentStep === STEPS.ERROR) return 'Feedback Failure';
    return '';
  };

  const handleSubmit = async () => {
    if (!selectedOption) {
      return;
    }

    setCurrentStep(STEPS.SUBMITTING);
    const requestBody = {
      pid: playerId || '',
      comment,
      reason_leaving: selectedOption,
      context: buildFeedbackContext(locale),
    };

    try {
      await submitFeedback.mutateAsync(requestBody);

      handleLeaveForLegacy();
      window.location.assign(config.register.redirectUrl);
    } catch {
      setCurrentStep(STEPS.ERROR);
      return;
    }
  };

  const handleBack = () => {
    setCurrentStep(STEPS.OPTIONS);
  };

  const feedbackOptions = getFeedbackOptions(t);

  const footer =
    currentStep === STEPS.OPTIONS ? (
      <div className="text-center flex-shrink-0">
        <Link href={config.register.redirectUrl} data-track-name="Skip" onClick={handleLeaveForLegacy}>
          {t('options.skip_link')}
        </Link>
      </div>
    ) : null;

  return (
    <div {...props}>
      <FeedbackSheet
        footer={footer}
        open={open}
        onOpenChange={handleOpenChange}
        showBackButton={currentStep === STEPS.COMMENT}
        onBack={handleBack}
        title={t('title')}
        data-track-feature="Feedback"
        data-track-container={getTrackContainer()}
        dataTestIdBaseName="why-leaving"
      >
        {currentStep === STEPS.OPTIONS && <Options options={feedbackOptions} selectedOption={selectedOption} onOptionClick={handleOptionClick} title={t('options.title')} />}

        {currentStep === STEPS.COMMENT && selectedOption && (
          <Comment
            selectedLabel={feedbackOptions.find(opt => opt.value === selectedOption)?.label}
            comment={comment}
            onCommentChange={handleCommentChange}
            onSubmit={handleSubmit}
            title={t('comment.title')}
            commentsLabel={t('comment.comments_label')}
            submitButtonText={t('comment.submit_button')}
          />
        )}

        {currentStep === STEPS.SUBMITTING && <FeedbackSuccess primaryText={t('feedback.success.primary')} secondaryText={t('feedback.success.secondary')} />}

        {currentStep === STEPS.ERROR && (
          <FeedbackError primaryText={t('feedback.error.primary')} buttonText={t('feedback.error.retry')} onRetry={() => setCurrentStep(STEPS.COMMENT)} />
        )}
      </FeedbackSheet>
    </div>
  );
};

export default WhyLeaving;
